import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Minimum number of data points apart required to trigger a zoom.
 * Prevents accidental zooms from simple clicks without meaningful drag distance.
 */
const MIN_ZOOM_DISTANCE = 2;

/**
 * Custom hook for drag-to-zoom functionality on Recharts time-series charts.
 *
 * Enables users to click-and-drag on any chart to select a time range.
 * When the selection completes, a callback fires with the start and end Date
 * objects derived from the chart data, allowing the parent component to update
 * all charts on the page to the zoomed time range.
 *
 * Each chart data point must include a `_ts` property (epoch milliseconds)
 * for the hook to compute the zoomed date range.
 *
 * Usage:
 * ```jsx
 * const { zoomState, getChartZoomProps } = useChartZoom({
 *     onZoomComplete: (from, to) => timeRangeSelectorRef.current?.setCustomRange(from, to)
 * });
 *
 * <BarChart data={chartData} {...getChartZoomProps('myChart', chartData)}>
 *     {zoomState.selecting && zoomState.chartId === 'myChart' && (
 *         <ReferenceArea x1={zoomState.startLabel} x2={zoomState.endLabel} ... />
 *     )}
 * </BarChart>
 * ```
 *
 * @param {Object} options
 * @param {Function} options.onZoomComplete - Called with (fromDate: Date, toDate: Date)
 *   when a valid drag selection completes.
 * @returns {Object} { zoomState, getChartZoomProps }
 */
export default function useChartZoom({ onZoomComplete }) {
    /**
     * Tracks the visual state of the zoom selection for rendering ReferenceArea.
     * - selecting: whether a drag is in progress
     * - chartId: which chart the drag started on
     * - startLabel / endLabel: X-axis label values for ReferenceArea bounds
     */
    const [zoomState, setZoomState] = useState({
        selecting: false,
        chartId: null,
        startLabel: null,
        endLabel: null,
    });

    // Mutable refs for values that change rapidly during drag and are only
    // read synchronously in the mouseUp handler (avoids stale closures and
    // unnecessary re-renders of the useCallback dependency chain).
    const activeDataRef = useRef(null);
    const startIndexRef = useRef(null);
    const endIndexRef = useRef(null);
    const selectingRef = useRef(false);
    // Flag set by the chart-level mouseUp handler so the document-level
    // fallback listener knows the event was already processed.
    const handledRef = useRef(false);

    /**
     * Resets all zoom-related state and refs to their initial idle values.
     */
    const resetZoom = useCallback(() => {
        setZoomState({
            selecting: false,
            chartId: null,
            startLabel: null,
            endLabel: null,
        });
        activeDataRef.current = null;
        startIndexRef.current = null;
        endIndexRef.current = null;
        selectingRef.current = false;
    }, []);

    // Fallback: if the user releases the mouse outside the chart area,
    // Recharts' onMouseUp won't fire. A document-level listener catches
    // this and cancels the zoom. A short setTimeout ensures the chart's
    // own handler (which runs synchronously first) gets a chance to
    // complete the zoom before we check `selectingRef`.
    useEffect(() => {
        const handleDocumentMouseUp = () => {
            setTimeout(() => {
                if (selectingRef.current && !handledRef.current) {
                    resetZoom();
                }
                handledRef.current = false;
            }, 0);
        };
        document.addEventListener('mouseup', handleDocumentMouseUp);
        return () => document.removeEventListener('mouseup', handleDocumentMouseUp);
    }, [resetZoom]);

    /**
     * Returns Recharts-compatible event handler props for a single chart.
     * Spread these onto a BarChart / LineChart / AreaChart component.
     *
     * @param {string} chartId - Unique identifier for this chart instance
     *   (used to conditionally render ReferenceArea on the active chart).
     * @param {Array} chartData - The data array bound to this chart.
     *   Each element must include a `_ts` property (epoch ms).
     * @returns {Object} { onMouseDown, onMouseMove, onMouseUp }
     */
    const getChartZoomProps = useCallback((chartId, chartData) => ({
        onMouseDown: (e) => {
            // Recharts provides activeTooltipIndex when the mouse is over a data point.
            // If null/undefined, the click is outside the plotted area — ignore it.
            if (e?.activeTooltipIndex == null) return;
            activeDataRef.current = chartData;
            startIndexRef.current = e.activeTooltipIndex;
            endIndexRef.current = e.activeTooltipIndex;
            selectingRef.current = true;
            setZoomState({
                selecting: true,
                chartId,
                startLabel: e.activeLabel,
                endLabel: e.activeLabel,
            });
        },
        onMouseMove: (e) => {
            if (!selectingRef.current) return;
            if (e?.activeLabel != null) {
                endIndexRef.current = e.activeTooltipIndex;
                setZoomState(prev => ({
                    ...prev,
                    endLabel: e.activeLabel,
                }));
            }
        },
        onMouseUp: (e) => {
            if (!selectingRef.current) return;
            // Mark as handled so the document-level fallback listener doesn't cancel.
            handledRef.current = true;

            const data = activeDataRef.current;
            const startIdx = startIndexRef.current;
            const endIdx = e?.activeTooltipIndex ?? endIndexRef.current;

            if (data && startIdx != null && endIdx != null) {
                const minIdx = Math.min(startIdx, endIdx);
                const maxIdx = Math.max(startIdx, endIdx);

                // Only zoom if the user dragged across enough data points
                // to represent a meaningful time range.
                if (maxIdx - minIdx >= MIN_ZOOM_DISTANCE) {
                    const fromTs = data[minIdx]?._ts;
                    const toTs = data[maxIdx]?._ts;

                    if (fromTs && toTs && fromTs !== toTs) {
                        onZoomComplete?.(new Date(fromTs), new Date(toTs));
                    }
                }
            }

            resetZoom();
        },
    }), [onZoomComplete, resetZoom]);

    return {
        zoomState,
        getChartZoomProps,
    };
}
